/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { ConfigProvider, Layout } from 'antd';
import moment from 'moment';
import { IntlProvider } from 'react-intl';
import StyledColoredButton from 'components/StyledColoredButton';
import Translated from 'components/Translated';
import { ORANGE } from 'assets/styles/settings';
import { PUBLIC_URLS, getAccess, checkExpirationDate, getWidth } from 'utils';
import { useNavigatorOnLine } from 'utils/hooks';
import AppLocale from '../../locales';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { StyledDrawer, StyledFooter, StyledBackTop } from './MainLayout.styled';

const year = new Date();

const MainLayout = ({
    children,
    name,
    roles,
    userPermissions,
    menuCollapsed,
    reseller,
    menuMobile,
    locale,
    userToken,
    userData,
    match,
    toggleMobileMenu,
    userLogout,
}) => {
    const [visible, setVisible] = useState(false);

    useNavigatorOnLine(locale);

    useEffect(() => {
        moment.locale(locale);

        if (!checkExpirationDate(userToken.data?.expirationDate)) {
            userLogout('expired');
        }
    }, [locale, userLogout, userToken.data, match.path]);

    const mounted = useRef(false);
    useEffect(() => {
        const handleResize = () => {
            if (getWidth() > 994 && menuMobile) {
                toggleMobileMenu();
            }
        };

        window.addEventListener('resize', handleResize);

        if (!mounted.current) {
            mounted.current = true;
            handleResize();
        }

        return () => {
            mounted.current = false;
            window.removeEventListener('resize', handleResize);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuMobile]);

    function handleMobile() {
        toggleMobileMenu();
    }

    function handleScroll(e) {
        // React 16 propagates scroll events from nested areas such as FullCalendar.
        if (e.target !== e.currentTarget) {
            return;
        }

        const { scrollTop } = e.currentTarget;
        setVisible(scrollTop > 100);
    }

    function handleGoToTop() {
        document.documentElement.scrollTo(0, 0);
    }

    const currentLocale = AppLocale[locale];
    const isOperatorWithoutMachine = userData.data?.user_group[0].name === 'operator' && userData.operator?.machine === null;

    if (!userToken.data || isOperatorWithoutMachine) {
        return <Redirect to={`/?referer=${window.location.pathname}`} />;
    }

    const userRoles = getAccess(userPermissions, name);

    if (!PUBLIC_URLS.includes(name)) {
        if (!userRoles.includes(roles)) {
            return <Redirect to="/401" />;
        }
    }

    const modifiedChildren = React.Children.map(children, (child, index) => React.cloneElement(child, {
        index,
        userRoles,
    }));

    const resellerName = reseller ? reseller.data.hits[0]?.name : '';

    return (
        <ConfigProvider locale={currentLocale.antd}>
            <IntlProvider locale={currentLocale.locale} messages={currentLocale.messages}>
                <Layout className={`gx-app-layout ${menuCollapsed ? '__collapsed' : ''}`}>
                    <Layout.Sider
                        theme="lite"
                        trigger={null}
                        collapsible
                        collapsed={menuCollapsed}
                        className="gx-app-sidebar gx-layout-sider-lite"
                    >
                        <StyledDrawer
                            className="gx-drawer-sidebar gx-drawer-sidebar-lite"
                            placement="left"
                            closable={false}
                            visible={menuMobile}
                            onClose={handleMobile}
                        >
                            <Sidebar />
                        </StyledDrawer>

                        <Sidebar />
                    </Layout.Sider>


                    <Layout id="layout" onScroll={handleScroll}>
                        <Topbar />

                        <Layout.Content className="gx-container-wrap gx-main-content-wrapper">
                            {modifiedChildren}

                            <StyledFooter>
                                Copyright &copy; {year.getFullYear()}. {resellerName}. <Translated id="global.copyright" />
                            </StyledFooter>
                        </Layout.Content>
                    </Layout>

                    <StyledBackTop visible={visible} onClick={handleGoToTop}>
                        <StyledColoredButton shape="circle" bg={ORANGE}>
                            <i className="la la-arrow-up" />
                        </StyledColoredButton>
                    </StyledBackTop>
                </Layout>
            </IntlProvider>
        </ConfigProvider>
    );
};

export default MainLayout;
